import { analytics } from "./analytics";
import { inDnd } from "./prefs";
import { plainText } from "./format";

export const notifSupported = () => typeof window !== "undefined" && "Notification" in window;
export const notifPermission = () => (notifSupported() ? Notification.permission : "unsupported");

export const requestNotifPermission = async () => {
  if (!notifSupported()) return "unsupported";
  const p = await Notification.requestPermission();
  analytics.track("notification_permission", { category: "notifications", label: p });
  window.dispatchEvent(new Event("tc:notif-permission"));
  return p;
};

let audioCtx = null;
export const playChime = () => {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const t = audioCtx.currentTime;
    [[880, 0], [1174.66, 0.09]].forEach(([freq, off]) => {
      const o = audioCtx.createOscillator();
      const g = audioCtx.createGain();
      o.type = "sine";
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, t + off);
      g.gain.exponentialRampToValueAtTime(0.18, t + off + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, t + off + 0.35);
      o.connect(g).connect(audioCtx.destination);
      o.start(t + off);
      o.stop(t + off + 0.4);
    });
  } catch { /* autoplay blocked */ }
};

const keywordHit = (text, keywords) => {
  const low = (text || "").toLowerCase();
  return (keywords || []).find((k) => k && low.includes(k.toLowerCase())) || null;
};

// Decide whether an incoming message should alert this user. Returns a reason string or null.
export const notifyReason = (m, { meId, prefs, conversation, parent, mentionsMe }) => {
  if (!m || m.user_id === meId || m.subtype === "system" || m.subtype === "huddle") return null;
  if (prefs.notify === "none") return null;
  if (prefs.notify === "all") return "all";
  if (conversation && conversation.type !== "channel") return "dm";
  if (mentionsMe) return "mention";
  const kw = keywordHit(m.text, prefs.keywords);
  if (kw) return `keyword:${kw}`;
  if (m.parent_id && prefs.thread_replies && parent && (parent.user_id === meId || (parent.reply_users || []).includes(meId))) return "thread";
  return null;
};

export const showDesktopNotification = ({ title, body, icon, tag, onClick }) => {
  if (!notifSupported() || Notification.permission !== "granted") return null;
  try {
    const n = new Notification(title, { body, icon: icon || "/favicon.ico", tag, silent: true });
    n.onclick = () => { window.focus(); onClick?.(); n.close(); };
    setTimeout(() => n.close(), 8000);
    return n;
  } catch {
    return null;
  }
};

export const buildNotification = (m, conversation, sender) => {
  const who = m.bot_name || sender?.display_name || m.user?.display_name || "Someone";
  const where = conversation ? (conversation.type === "channel" ? `#${conversation.name || conversation.display_name}` : null) : null;
  const title = where ? `${who} in ${where}` : who;
  let body = plainText(m.text || "").replace(/\s+/g, " ").trim();
  if (!body && m.files?.length) body = `📎 ${m.files.length === 1 ? m.files[0].name : `${m.files.length} files`}`;
  if (m.subtype === "email" && m.meta?.email) body = `✉️ ${m.meta.email.subject}${body ? ` — ${body}` : ""}`;
  if (body.length > 140) body = `${body.slice(0, 137)}…`;
  return { title, body: body || "New message", icon: sender?.avatar_url || m.user?.avatar_url };
};

export const shouldSuppress = (prefs) => inDnd(prefs.dnd);
